import { Button, Group, Loader, rem, Select, Stack, TextInput, Title } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { httpPostGraphql } from "shared/api/httpClient";
import { useUpdateUser } from "shared/graphql/customer";
import { useUser } from "shared/graphql/users";
import { useUserId } from "shared/stores/oidc";
import { titlecase } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Language } from "src/gql/graphql";

const EditNameForm = () => {
    const userInfo = useUserId();
    const { user } = useUser();

    const { updateUserAsync } = useUpdateUser();
    const [newName, setNewName] = useDebouncedState(user?.name ?? "", 500);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (newName == user?.name || newName == "") {
            setIsLoading(false);
            return;
        }
        updateUserAsync({
            userId: userInfo.userId,
            name: newName,
            language: user?.language ?? Language.English,
        })
            .then(() => setIsLoading(false))
            .catch(notify.catch);
    }, [newName]);

    return (
        <Group align="flex-end">
            <TextInput
                label="Name"
                w="min-content"
                miw={rem(200)}
                defaultValue={user?.name ?? undefined}
                placeholder="Enter name..."
                required
                onChange={(e) => {
                    setNewName(e.currentTarget.value);
                    setIsLoading(true);
                }}
                rightSection={isLoading && <Loader type="oval" mx={0} size={16} />}
            />
        </Group>
    );
};

export const ProfileContainer = () => {
    const auth = useAuth();
    const { userId } = useUserId();
    const { languages } = useLanguages();
    const { user } = useUser();
    const { updateUser } = useUpdateUser();

    return (
        <Stack>
            <Title order={4}>Preferred Name</Title>
            <EditNameForm />
            <Select
                allowDeselect={false}
                label="Language preference"
                required
                defaultValue={user?.language}
                data={languages.map((l) => ({ label: titlecase(l as string), value: l }))}
                w="min-content"
                miw={rem(300)}
                onChange={(_, opt) => {
                    updateUser({
                        userId,
                        name: user?.name ?? "",
                        language: opt.value as Language,
                    });
                }}
            />
            <a href={`${auth.settings.authority}/account`} target="_blank" style={{ width: "fit-content" }}>
                <Button variant="default">Edit Sign-in Information</Button>
            </a>
        </Stack>
    );
};

export function useLanguages() {
    const {
        data: {
            customer: { languages },
        },
    } = useSuspenseQuery(languageQueryOptions);
    return { languages };
}

export const languageQueryOptions = queryOptions({
    queryKey: qk("customer", "languages"),
    queryFn: () => httpPostGraphql(languageQuery, {}),
});

const languageQuery = graphql(`
    query ProfileGetLanguages {
        customer {
            languages
        }
    }
`);
